/**
 * Prompt Templates Page
 * Management page for system prompt templates
 */

import { useState } from 'react';
import { Plus, AlertCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/i18n';
import {
  TemplateList,
  TemplateEditor,
  usePromptTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  useSetDefaultTemplate,
  useAddSection,
  useUpdateSection,
  useDeleteSection,
  useReorderSections,
} from '@/features/prompt-templates';
import type { PromptTemplate, CreateTemplateRequest, UpdateTemplateRequest, PromptSection, SectionOrderItem } from '@/types/promptTemplates';

export function PromptTemplatesPage() {
  const { t } = useI18n();
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<PromptTemplate | null>(null);

  // Queries
  const { data: templates = [], isLoading, error, refetch } = usePromptTemplates(true);

  // Mutations
  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();
  const setDefaultMutation = useSetDefaultTemplate();
  const addSectionMutation = useAddSection();
  const updateSectionMutation = useUpdateSection();
  const deleteSectionMutation = useDeleteSection();
  const reorderSectionsMutation = useReorderSections();

  // Sort templates: default first, then by name
  const sortedTemplates = [...templates].sort((a, b) => {
    if (a.is_default && !b.is_default) return -1;
    if (!a.is_default && b.is_default) return 1;
    return a.name.localeCompare(b.name);
  });

  const handleCreate = async () => {
    const newTemplate: CreateTemplateRequest = {
      name: `New Template ${templates.length + 1}`,
      description: 'A new prompt template',
      separator: '\n\n',
      is_default: false,
      sections: [],
    };

    try {
      const created = await createMutation.mutateAsync(newTemplate);
      setSelectedTemplate(created);
    } catch (error) {
      console.error('Failed to create template:', error);
    }
  };

  const handleDuplicate = async (template: PromptTemplate) => {
    const duplicateData: CreateTemplateRequest = {
      name: `${template.name} (Copy)`,
      description: template.description,
      separator: template.separator,
      is_default: false,
      sections: template.sections.map(s => ({
        id: s.id,
        name: s.name,
        type: s.type,
        order: s.order,
        enabled: s.enabled,
        source: s.source,
        wrap_tag: s.wrap_tag,
        template: s.template,
        description: s.description,
      })),
    };

    try {
      const created = await createMutation.mutateAsync(duplicateData);
      setSelectedTemplate(created);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    }
  };

  const handleSave = async (data: UpdateTemplateRequest) => {
    if (!selectedTemplate) return;

    try {
      const updated = await updateMutation.mutateAsync({
        templateId: selectedTemplate.id,
        data,
      });
      setSelectedTemplate(updated);
    } catch (error) {
      console.error('Failed to save template:', error);
      throw error;
    }
  };

  const handleSetDefault = async (template?: PromptTemplate) => {
    const targetTemplate = template || selectedTemplate;
    if (!targetTemplate) return;

    try {
      await setDefaultMutation.mutateAsync(targetTemplate.id);
      await refetch();
      if (selectedTemplate?.id === targetTemplate.id) {
        const updated = templates.find(t => t.id === targetTemplate.id);
        if (updated) setSelectedTemplate({ ...updated, is_default: true });
      }
    } catch (error) {
      console.error('Failed to set default template:', error);
      throw error;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMutation.mutateAsync(deleteConfirm.id);
      setDeleteConfirm(null);
      if (selectedTemplate?.id === deleteConfirm.id) {
        setSelectedTemplate(null);
      }
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const handleAddSection = async (section: PromptSection) => {
    if (!selectedTemplate) return;

    // Auto-calculate order: place new section at the end
    const maxOrder = selectedTemplate.sections.reduce(
      (max, s) => Math.max(max, s.order),
      0
    );
    const sectionWithOrder = {
      ...section,
      order: maxOrder + 10,
    };

    try {
      const updated = await addSectionMutation.mutateAsync({
        templateId: selectedTemplate.id,
        section: sectionWithOrder,
      });
      setSelectedTemplate(updated);
    } catch (error) {
      console.error('Failed to add section:', error);
      throw error;
    }
  };

  const handleUpdateSection = async (sectionId: string, section: PromptSection) => {
    if (!selectedTemplate) return;

    try {
      const updated = await updateSectionMutation.mutateAsync({
        templateId: selectedTemplate.id,
        sectionId,
        section,
      });
      setSelectedTemplate(updated);
    } catch (error) {
      console.error('Failed to update section:', error);
      throw error;
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!selectedTemplate) return;

    try {
      const updated = await deleteSectionMutation.mutateAsync({
        templateId: selectedTemplate.id,
        sectionId,
      });
      setSelectedTemplate(updated);
    } catch (error) {
      console.error('Failed to delete section:', error);
      throw error;
    }
  };

  const handleReorderSections = async (orders: SectionOrderItem[]) => {
    if (!selectedTemplate) return;

    try {
      const updated = await reorderSectionsMutation.mutateAsync({
        templateId: selectedTemplate.id,
        orders,
      });
      setSelectedTemplate(updated);
    } catch (error) {
      console.error('Failed to reorder sections:', error);
      throw error;
    }
  };

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    setDefaultMutation.isPending ||
    addSectionMutation.isPending ||
    updateSectionMutation.isPending ||
    deleteSectionMutation.isPending ||
    reorderSectionsMutation.isPending;

  // Show editor if template is selected
  if (selectedTemplate) {
    return (
      <TemplateEditor
        template={selectedTemplate}
        onBack={() => setSelectedTemplate(null)}
        onSave={handleSave}
        onSetDefault={() => handleSetDefault()}
        onAddSection={handleAddSection}
        onUpdateSection={handleUpdateSection}
        onDeleteSection={handleDeleteSection}
        onReorderSections={handleReorderSections}
        isSaving={isMutating}
      />
    );
  }

  // Show list view
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary-600" />
            <h1 className="text-2xl font-bold text-gray-900">{t('promptTemplatesPage.pageTitle')}</h1>
          </div>
          <Button variant="primary" onClick={handleCreate} loading={createMutation.isPending}>
            <Plus className="w-4 h-4 mr-2" />
            {t('promptTemplatesPage.createTemplate')}
          </Button>
        </div>
        <p className="text-gray-600">
          {t('promptTemplatesPage.pageDescription')}
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-800 font-semibold mb-1">{t('promptTemplatesPage.errorLoading')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
            <p className="text-gray-500">{t('promptTemplatesPage.loadingTemplates')}</p>
          </div>
        </div>
      )}

      {/* Template List */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <TemplateList
            templates={sortedTemplates}
            onSelect={setSelectedTemplate}
            onDelete={setDeleteConfirm}
            onSetDefault={handleSetDefault}
            onDuplicate={handleDuplicate}
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('promptTemplatesPage.deleteTemplate')}</h3>
                <p className="text-gray-600 mt-1">
                  {t('promptTemplatesPage.deleteConfirm')} <strong>{deleteConfirm.name}</strong>?
                  {' '}{t('promptTemplatesPage.cannotUndo')}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
